import { ModalBaseDirective } from '$core/base/_modal.component';
import { focusFirstInvalidField } from '$core/helpers/focus-first-invaild-field';
import { handleFormHttpError } from '$core/helpers/server-errors-form-handler';
import { UserTypes, UserTypesMap } from '$core/models/user';
import {
  AuthenticationService,
  Credentials,
} from '$core/services/auth.service';
import { UserService } from '$core/services/user.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { transition, trigger, useAnimation } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fadeIn } from 'ng-animate';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { first } from 'rxjs/operators';
import { AuthParamsValues } from '$core/models/auth-modals-query-params';
import { AuthModalsService } from '$core/services/auth-modals.service';
import { RegistrationModalComponent } from '$core/modal/registration-modal/registration-modal.component';
import { ForgotPasswordModalComponent } from '$core/modal/forgot-password-modal/forgot-password-modal.component';
import { NotificationsDataService } from '$modules/personal/notifications/notifications-data.service';

@Component({
  selector: 'app-login-modal',
  templateUrl: './login-modal.component.html',
  animations: [
    trigger('parent', [transition(':enter', [])]),
    trigger('fadeInOut', [
      transition(':enter', useAnimation(fadeIn), {
        params: { timing: 0.2 },
      }),
    ]),
  ],
})
export class LoginModalComponent extends ModalBaseDirective {
  @ViewChild('form', { static: false }) public form: NgForm;
  @ViewChild('formEl', { static: false })
  public formEl: ElementRef<HTMLFormElement>;
  @ViewChild('formEmail', { static: false }) public formEmail: NgForm;
  @ViewChild('formElEmail', { static: false })
  public formEmailEl: ElementRef<HTMLFormElement>;

  private _phone: string;
  public get phone(): string {
    return this._phone;
  }

  public set phone(v: string) {
    this._phone = v ? v.replace(/\D/g, '') : '';
  }

  public tpi: string;
  public filial: string;
  public password: string;
  public email: string;

  public types = [UserTypes.Individual, UserTypes.Legal];
  public typesMap = UserTypesMap;
  public activeType = this.types[0];

  public isResendConfirmationAvailable = false;
  private _isEmailMissing = false;
  public get isEmailMissing() {
    return this._isEmailMissing;
  }

  public set isEmailMissing(value) {
    this._isEmailMissing = value;
    this.password = '';
  }

  constructor(
    protected modalRef: BsModalRef,
    protected modalService: BsModalService,
    protected translateService: TranslateService,
    private authService: AuthenticationService,
    private userService: UserService,
    private swalService: SwalService,
    private authModalsService: AuthModalsService,
    private notificationsDataService: NotificationsDataService
  ) {
    super(modalRef, modalService, translateService);
  }

  public async sendLogin() {
    if (this.form.invalid) {
      focusFirstInvalidField(this.formEl);
      return;
    }

    const body: Credentials = {
      login: this.activeType === UserTypes.Individual ? this.phone : this.tpi,
      password: this.password,
      type: this.activeType,
    };

    if (this.activeType === UserTypes.Legal) {
      body.filial = this.filial || null;
    }

    body.type = this.activeType;
    this.isLoading = true;
    this.isResendConfirmationAvailable = false;
    try {
      await this.authService.login(body).toPromise();
      this.notificationsDataService.refresh$.next();
      this.hide();
    } catch (error) {
      const res: HttpErrorResponse = error;
      if (res.status === 422) {
        if (res.error.errors.email) {
          this.isResendConfirmationAvailable = true;
        }
        handleFormHttpError(res, this.form);
        focusFirstInvalidField(this.formEl);
      } else {
        throw res;
      }
    } finally {
      this.isLoading = false;
    }
  }

  public async resendEmail() {
    const form = this.formEmail;
    const formEl = this.formEmailEl;

    if (form.invalid) {
      focusFirstInvalidField(formEl);
      return;
    }
    const body: any = {
      login: this.tpi,
      email: this.email,
    };

    if (this.activeType === UserTypes.Legal) {
      body.filial = this.filial || null;
    }

    this.isLoading = true;
    try {
      await this.userService.confirmationEmailResend(body).toPromise();
      this.hide();
      this.modalService.onHidden.pipe(first()).subscribe(() => {
        const swal = this.swalService.swal;
        swal.title = this.translate('CONFIRMATION_EMAIL_SENT');
        swal.icon = 'success';
        swal.fire();
      });
    } catch (error) {
      this.handleError(error as HttpErrorResponse, form, formEl);
    } finally {
      this.isLoading = false;
    }
  }

  public openRegistration() {
    this.hide();
    const initialState = this.transferState();
    this.authModalsService.show(
      RegistrationModalComponent,
      this.activeType,
      AuthParamsValues.REGISTRATION,
      initialState
    );
  }

  public openForgotPassword() {
    this.hide();
    const initialState = this.transferState();
    this.authModalsService.show(
      ForgotPasswordModalComponent,
      this.activeType,
      AuthParamsValues.FORGOT_PASSWORD,
      initialState
    );
  }

  private transferState(): {
    activeType: LoginModalComponent['activeType'];
    tpi?: LoginModalComponent['tpi'];
    filial?: LoginModalComponent['filial'];
  } {
    const initialState = {
      activeType: this.activeType,
    };

    if (this.activeType === UserTypes.Legal) {
      return {
        ...initialState,
        tpi: this.tpi,
        filial: this.filial,
      };
    }

    return initialState;
  }

  public changeTab(type: UserTypes) {
    this.authModalsService.setUserTypeQueryParam(type);
    this.activeType = type;
  }

  public hide(): void {
    this.authModalsService.hide();
    super.hide();
  }
}
